import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight } from "lucide-react";
import Seo from "@/components/site/Seo";
import JsonLd from "@/components/site/JsonLd";
import BookCall from "@/components/site/BookCall";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CaseStudies() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/case-studies`).then((r) => setItems(r.data)).finally(() => setLoading(false));
  }, []);

  return (
    <>
      <Seo
        title="Case Studies | B2B Marketing Automation Outcomes | Xadentra"
        description="Explore Xadentra case studies across Adobe Marketo Engage, Salesforce, campaign operations, reporting, and B2B marketing automation."
        path="/case-studies"
      />
      <JsonLd
        id="case-studies-breadcrumb"
        data={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://xadentra.com/" },
            { "@type": "ListItem", position: 2, name: "Case Studies", item: "https://xadentra.com/case-studies" },
          ],
        }}
      />

      <section className="bg-[#07111F] text-white relative overflow-hidden">
        <div className="xa-blob" style={{ width: 480, height: 480, background: "#6D4DFF", top: -180, left: -100, opacity: 0.5 }} />
        <div className="xa-blob" style={{ width: 380, height: 380, background: "#00D4FF", bottom: -120, right: -120, opacity: 0.32 }} />
        <div className="absolute inset-0 xa-grid-dark opacity-50 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 lg:py-32 relative">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#00D4FF]">Engagement Playbooks</div>
          <h1 className="font-display mt-6 text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight leading-[1.1] sm:leading-[1.05] lg:leading-[0.98] max-w-4xl">
            Real marketing operations<br />work — from the field.
          </h1>
          <p className="mt-8 text-lg text-white/70 max-w-3xl leading-relaxed">
            Xadentra is a new agency, but the engagement patterns below aren&apos;t.
            They&apos;re the kind of Marketo, Salesforce, lifecycle, and reporting
            work our senior team has shipped repeatedly inside B2B SaaS and enterprise
            marketing teams. Every problem, fix, tool, and outcome below is real —
            the operating approach, the format, and the shape of results you can
            expect from us.
          </p>
          <p className="mt-4 text-sm text-white/50 max-w-3xl">
            Each playbook is structured the same way: <span className="text-white/75">Problem · What we fixed · Tools · Outcome · Why it mattered.</span>
          </p>
        </div>
      </section>

      <section className="bg-[#F7F9FC] border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
          {loading ? (
            <div className="text-[#6B7280]">Loading case studies…</div>
          ) : (
            <div className="space-y-6">
              {items.map((c, i) => (
                <Link
                  to={`/case-studies/${c.slug}`}
                  key={c.slug}
                  data-testid={`case-study-${i}`}
                  className="block bg-white border border-[#E5E7EB] rounded-2xl p-8 lg:p-12 grid grid-cols-1 lg:grid-cols-12 gap-8 hover:shadow-[0_24px_60px_-30px_rgba(7,17,31,0.18)] hover:border-[#6D4DFF]/40 transition-all group"
                >
                  <div className="lg:col-span-3">
                    <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#6D4DFF]">{c.industry}</div>
                    <div className="mt-2 font-display text-xl font-semibold text-[#07111F]">{c.client}</div>
                    <div className="mt-6 flex flex-wrap gap-2">
                      {c.services?.map((s, j) => (
                        <span key={j} className="text-[11px] font-mono tracking-wider px-2.5 py-1 bg-[#F7F9FC] border border-[#E5E7EB] rounded-full text-[#6B7280]">
                          {s}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="lg:col-span-9 space-y-5">
                    <h2 className="font-display text-2xl lg:text-3xl font-bold tracking-tight text-[#07111F] leading-tight group-hover:text-[#6D4DFF] transition-colors">
                      {c.headline}
                    </h2>
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#6D4DFF]">Problem</div>
                      <p className="mt-2 text-sm text-[#374151] leading-relaxed">
                        {c.problem || c.challenge}
                      </p>
                    </div>
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#3EE8A5]">Outcome</div>
                      <p className="mt-2 text-sm text-[#374151] leading-relaxed">
                        {c.outcome_summary || c.impact}
                      </p>
                    </div>
                    <span
                      data-testid={`case-study-cta-${i}`}
                      className="mt-2 inline-flex items-center gap-2 text-sm font-medium text-[#6D4DFF] group-hover:gap-3 transition-all"
                    >
                      Read the playbook <ArrowUpRight className="w-4 h-4" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="bg-[#07111F] text-white relative overflow-hidden">
        <div className="xa-blob pointer-events-none" style={{ width: 460, height: 460, background: "#6D4DFF", top: -160, left: -120, opacity: 0.4 }} />
        <div className="xa-blob pointer-events-none" style={{ width: 380, height: 380, background: "#00D4FF", bottom: -140, right: -100, opacity: 0.25 }} />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 grid grid-cols-1 lg:grid-cols-12 gap-8 items-end relative">
          <div className="lg:col-span-8">
            <h2 className="font-display text-4xl lg:text-5xl font-bold tracking-tight">
              Your engagement could be next.
            </h2>
          </div>
          <BookCall source="case_studies_cta" className="lg:col-span-4 lg:justify-self-end">
            Book a meeting
          </BookCall>
        </div>
      </section>
    </>
  );
}
